import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  Mountain,
  CheckCircle2,
  ShieldCheck,
  Layers,
  Wrench,
  ClipboardCheck,
  ArrowUpRight,
  MapPin,
} from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";
import StickyCTA from "@/components/site/StickyCTA";
import { useReveal } from "@/lib/reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1517089596392-fb9a9033e05b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&auto=format&fit=crop";

const reasons = [
  {
    icon: ShieldCheck,
    title: "Éviter les fissures",
    desc: "Un sol mal connu provoque tassements différentiels, infiltrations et fissures structurelles parfois irréversibles.",
  },
  {
    icon: Layers,
    title: "Adapter les fondations",
    desc: "Selon la nature du terrain (volcanique, argileux, rocheux, en pente), la solution de fondation doit être ajustée.",
  },
  {
    icon: CheckCircle2,
    title: "Sécuriser la construction",
    desc: "Une étude de sol valide la faisabilité, anticipe les contraintes et protège votre investissement sur le long terme.",
  },
];

const services = [
  {
    icon: Mountain,
    title: "Analyse de terrain",
    desc: "Sondages, prélèvements et caractérisation géotechnique de votre parcelle à La Réunion.",
    bullets: ["Reconnaissance visuelle", "Sondages géotechniques", "Mesures in situ", "Rapport d'étude détaillé"],
  },
  {
    icon: Wrench,
    title: "Études G1 à G5 / Assainissement",
    desc: "Toutes les missions normées (NF P 94-500), de l'étude préalable au suivi d'exécution, plus l'assainissement non collectif (SPANC).",
    bullets: ["G1 — étude préalable", "G2 — étude de conception", "G3, G4, G5 — exécution", "Étude SPANC"],
  },
  {
    icon: ClipboardCheck,
    title: "Accompagnement technique",
    desc: "Interprétation, recommandations et coordination avec votre dossier de permis. Un seul interlocuteur jusqu'au dépôt.",
    bullets: ["Synthèse pour permis", "Recommandations fondations", "Dimensionnement assainissement", "Suivi de projet"],
  },
];

const cities = [
  "Saint-Denis", "Saint-Paul", "Saint-Pierre", "Le Tampon", "Saint-André",
  "Saint-Louis", "Saint-Benoît", "Le Port", "La Possession", "Saint-Joseph",
  "Sainte-Marie", "Sainte-Suzanne", "Bras-Panon", "L'Étang-Salé", "Petite-Île",
  "Saint-Leu", "Saint-Philippe", "Sainte-Rose", "Salazie", "Cilaos",
  "Entre-Deux", "Trois-Bassins", "Les Avirons",
];

const faq = [
  {
    q: "Quelle est la différence entre G1, G2, G3, G4 et G5 ?",
    a: "G1 = étude préalable de site. G2 = étude de conception (avant-projet/projet). G3 = étude et suivi d'exécution. G4 = supervision géotechnique. G5 = diagnostic spécifique. Chaque mission correspond à une phase de votre projet.",
  },
  {
    q: "L'étude de sol est-elle obligatoire à La Réunion ?",
    a: "Une étude de sol est fortement recommandée pour toute construction. Elle est de plus en plus exigée pour le permis de construire et obligatoire pour l'assainissement non collectif (SPANC).",
  },
  {
    q: "Combien de temps prend une étude de sol ?",
    a: "Selon la mission et l'accessibilité du terrain, une étude de sol prend généralement 2 à 6 semaines, sondages et rapport inclus.",
  },
];

export default function EtudeSolLaReunion() {
  useReveal();

  useEffect(() => {
    document.title = "Étude de sol à La Réunion (974) | Analyse terrain & accompagnement";
    setMeta(
      "description",
      "Réalisez votre étude de sol à La Réunion avec Solmis Habitat. Analyse complète, G1 à G5, projet sécurisé. Devis gratuit."
    );
    setMeta(
      "keywords",
      "étude de sol La Réunion, étude géotechnique La Réunion, G1 G2 G3 G4 G5, analyse terrain Réunion, bureau d'études Réunion, étude SPANC, fondations maison Réunion, sondage géotechnique"
    );
    setMetaProperty("og:title", "Étude de sol à La Réunion (974) | Solmis Habitat");
    setMetaProperty(
      "og:description",
      "Étude de sol G1 à G5 à La Réunion. Analyse complète du terrain, sécurisation du projet, accompagnement par un seul interlocuteur."
    );
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white text-foreground min-h-screen">
      <Navbar />

      <main>
        {/* Hero SEO */}
        <section className="relative overflow-hidden pt-36 md:pt-44 pb-20 md:pb-28">
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute -top-40 right-0 h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle_at_center,hsl(var(--brand-terra)/0.08),transparent_70%)]" />
          </div>

          <div className="container-wide relative grid gap-16 md:grid-cols-12 items-center">
            <div className="md:col-span-7 reveal">
              <div className="flex items-center gap-3 mb-6">
                <span className="rule" />
                <span className="overline">La Réunion · 974</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-[3.75rem] font-medium leading-[1.07] text-primary">
                Étude de sol à{" "}
                <span className="italic font-light text-[hsl(var(--brand-terra))]">
                  La Réunion
                </span>{" "}
                : sécurisez votre projet dès le départ.
              </h1>
              <p className="mt-6 max-w-xl text-lg sm:text-xl font-light text-primary/85 leading-snug">
                L'étude de sol est une étape essentielle pour tout projet de
                construction à La Réunion. Elle permet d'adapter votre projet
                aux caractéristiques du terrain et d'éviter des problèmes
                coûteux.
              </p>

              <p className="mt-5 max-w-xl text-base text-muted-foreground leading-relaxed border-l-2 border-[hsl(var(--brand-terra))] pl-4">
                Avant toute construction, l'étude de sol est une étape
                essentielle pour obtenir un{" "}
                <Link
                  to="/permis-de-construire-la-reunion"
                  className="text-[hsl(var(--brand-terra))] font-medium underline-offset-4 hover:underline"
                >
                  permis de construire conforme à La Réunion
                </Link>
                .
              </p>

              <div className="mt-9 flex flex-wrap gap-4">
                <Link to="/#contact" data-testid="es-cta-devis" className="btn-primary group">
                  Demander un devis
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link to="/#callback" data-testid="es-cta-rappel" className="btn-outline">
                  <Phone size={16} strokeWidth={1.6} />
                  Être rappelé
                </Link>
              </div>

              <p className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 size={13} strokeWidth={2} className="text-[hsl(var(--brand-terra))]" />
                  Devis gratuit et sans engagement
                </span>
                <span className="text-border">·</span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-[hsl(var(--brand-terra))]">⚡</span>
                  Réponse rapide sous 24h
                </span>
              </p>

              <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-xl">
                {["Études G1 à G5", "Toute La Réunion", "Devis gratuit"].map((t) => (
                  <div key={t} className="flex items-start gap-2.5">
                    <CheckCircle2 size={18} strokeWidth={1.5} className="text-[hsl(var(--brand-terra))] mt-0.5 shrink-0" />
                    <span className="text-sm text-primary/85 font-medium">{t}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="md:col-span-5 reveal">
              <div className="relative">
                <div className="absolute -top-4 -left-4 h-20 w-20 border-t border-l border-[hsl(var(--brand-terra))]" />
                <div className="absolute -bottom-4 -right-4 h-20 w-20 border-b border-r border-[hsl(var(--brand-terra))]" />
                <div className="relative aspect-[4/5] overflow-hidden bg-muted">
                  <img
                    src={HERO_IMG}
                    alt="Étude de sol et sondage géotechnique à La Réunion"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pourquoi */}
        <section className="py-20 md:py-28 bg-secondary border-y border-border">
          <div className="container-wide">
            <div className="max-w-3xl mb-14 reveal">
              <div className="flex items-center gap-3 mb-5">
                <span className="rule" />
                <span className="overline">Les enjeux</span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl text-primary leading-tight">
                Pourquoi faire une{" "}
                <span className="italic font-light">étude de sol</span> ?
              </h2>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {reasons.map((r, i) => {
                const Icon = r.icon;
                return (
                  <article
                    key={r.title}
                    className="reveal bg-white border border-border p-8 hover:-translate-y-1 transition-all duration-300 hover:border-[hsl(var(--brand-terra))]"
                    style={{ transitionDelay: `${i * 70}ms` }}
                  >
                    <span className="inline-flex h-12 w-12 items-center justify-center bg-[hsl(var(--brand-terra)/0.08)] text-[hsl(var(--brand-terra))] rounded-2xl">
                      <Icon size={22} strokeWidth={1.5} />
                    </span>
                    <h3 className="mt-6 font-display text-2xl text-primary leading-snug">
                      {r.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      {r.desc}
                    </p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Prestations */}
        <section className="py-20 md:py-28">
          <div className="container-wide">
            <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
              <div className="md:col-span-7 reveal">
                <div className="flex items-center gap-3 mb-5">
                  <span className="rule" />
                  <span className="overline">Nos prestations</span>
                </div>
                <h2 className="font-display text-4xl md:text-5xl text-primary leading-tight">
                  Ce que comprend une{" "}
                  <span className="italic font-light">étude de sol</span> avec
                  Solmis Habitat.
                </h2>
              </div>
              <div className="md:col-span-5 reveal">
                <p className="text-base text-muted-foreground leading-relaxed">
                  Bureau d'études et accompagnement technique à La Réunion :
                  nous prenons en charge l'analyse du terrain, les missions
                  géotechniques normées et la coordination avec votre projet de
                  construction.
                </p>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {services.map((s, i) => {
                const Icon = s.icon;
                return (
                  <article
                    key={s.title}
                    className="reveal group border border-border bg-white p-8 hover:-translate-y-1 transition-all duration-300 hover:border-[hsl(var(--brand-terra))]"
                    style={{ transitionDelay: `${i * 70}ms` }}
                  >
                    <div className="flex items-start justify-between mb-7">
                      <span className="inline-flex h-12 w-12 items-center justify-center bg-[hsl(var(--brand-terra)/0.08)] text-[hsl(var(--brand-terra))] rounded-2xl">
                        <Icon size={22} strokeWidth={1.5} />
                      </span>
                      <ArrowUpRight size={18} className="text-muted-foreground/40 group-hover:text-[hsl(var(--brand-terra))] transition-all duration-300" />
                    </div>
                    <h3 className="font-display text-2xl text-primary leading-tight">{s.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
                    <ul className="mt-6 space-y-2 border-t border-border pt-5">
                      {s.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2.5 text-sm text-primary/85">
                          <span className="mt-2 h-1 w-3 bg-[hsl(var(--brand-terra))] shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Mid CTA */}
        <section className="py-12 md:py-16 bg-primary text-primary-foreground">
          <div className="container-wide flex flex-col md:flex-row items-start md:items-center justify-between gap-8 reveal">
            <div className="max-w-2xl">
              <p className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--brand-terra))] font-semibold">
                Devis gratuit · sans engagement
              </p>
              <h3 className="mt-3 font-display text-3xl md:text-4xl leading-tight">
                Obtenez votre étude de sol{" "}
                <span className="italic font-light text-white/85">personnalisée</span>.
              </h3>
            </div>
            <Link
              to="/#contact"
              className="group inline-flex items-center gap-2 bg-[hsl(var(--brand-terra))] text-white px-8 py-5 text-sm font-medium tracking-wide rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:bg-[hsl(var(--brand-terra)/0.9)] shrink-0"
            >
              Demander mon devis
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </section>

        {/* Lien permis de construire */}
        <section className="py-20 md:py-28">
          <div className="container-narrow">
            <div className="grid md:grid-cols-12 gap-10 items-center reveal">
              <div className="md:col-span-7">
                <div className="flex items-center gap-3 mb-5">
                  <span className="rule" />
                  <span className="overline">Et le permis de construire ?</span>
                </div>
                <h2 className="font-display text-4xl md:text-5xl text-primary leading-tight">
                  Étude de sol{" "}
                  <span className="italic font-light text-[hsl(var(--brand-terra))]">&amp;</span>{" "}
                  permis de construire vont de pair.
                </h2>
                <p className="mt-6 text-base md:text-lg text-primary/85 leading-relaxed">
                  <strong>
                    Avant toute construction, l'étude de sol est une étape
                    essentielle pour obtenir un permis de construire conforme
                    à La Réunion.
                  </strong>{" "}
                  Elle anticipe les contraintes techniques et accélère
                  l'instruction administrative.
                </p>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                  Solmis Habitat coordonne les deux pour vous : étude technique
                  + dossier administratif, gérés par un seul interlocuteur à
                  La Réunion.
                </p>

                <Link
                  to="/permis-de-construire-la-reunion"
                  data-testid="link-permis-cta"
                  className="mt-9 group inline-flex items-center gap-2 bg-[hsl(var(--brand-terra))] text-white px-7 py-4 text-sm font-medium tracking-wide rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:bg-[hsl(var(--brand-terra)/0.9)]"
                >
                  Découvrir notre accompagnement pour votre permis de construire
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
              <div className="md:col-span-5">
                <Link
                  to="/permis-de-construire-la-reunion"
                  data-testid="link-permis"
                  className="block group border border-border bg-white p-8 hover:border-[hsl(var(--brand-terra))] hover:-translate-y-1 transition-all duration-300"
                >
                  <p className="overline mb-3">Page dédiée</p>
                  <h3 className="font-display text-2xl text-primary leading-tight">
                    Permis de construire à La Réunion
                  </h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    Découvrez notre offre complète pour le montage et le dépôt
                    de votre dossier de permis.
                  </p>
                  <p className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[hsl(var(--brand-terra))] transition-transform duration-300 group-hover:translate-x-1">
                    En savoir plus
                    <ArrowRight size={16} />
                  </p>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Zones d'intervention */}
        <section className="py-20 md:py-28 bg-secondary border-y border-border">
          <div className="container-wide">
            <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
              <div className="md:col-span-7 reveal">
                <div className="flex items-center gap-3 mb-5">
                  <span className="rule" />
                  <span className="overline">Zones d'intervention</span>
                </div>
                <h2 className="font-display text-4xl md:text-5xl text-primary leading-tight">
                  Étude de sol partout à{" "}
                  <span className="italic font-light">La Réunion</span>.
                </h2>
              </div>
              <div className="md:col-span-5 reveal">
                <p className="text-base text-muted-foreground leading-relaxed">
                  Nous intervenons sur l'ensemble du département (974) — côte
                  Ouest, Sud, Est, Nord et hauts inclus.
                </p>
              </div>
            </div>

            <ul className="reveal grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {cities.map((c) => (
                <li
                  key={c}
                  className="flex items-center gap-2.5 bg-white border border-border px-4 py-3 text-sm text-primary/85 hover:border-[hsl(var(--brand-terra))] transition-colors"
                >
                  <MapPin size={14} strokeWidth={1.5} className="text-[hsl(var(--brand-terra))] shrink-0" />
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ SEO */}
        <section className="py-20 md:py-28">
          <div className="container-narrow">
            <div className="max-w-3xl mb-12 reveal">
              <div className="flex items-center gap-3 mb-5">
                <span className="rule" />
                <span className="overline">FAQ</span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl text-primary leading-tight">
                Questions fréquentes sur l'{" "}
                <span className="italic font-light">étude de sol</span>.
              </h2>
            </div>

            <div className="border-t border-border">
              {faq.map((f, i) => (
                <details
                  key={f.q}
                  className="group border-b border-border py-6"
                >
                  <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
                    <h3 className="font-display text-xl md:text-2xl text-primary leading-snug">
                      {f.q}
                    </h3>
                    <span className="inline-flex h-8 w-8 items-center justify-center border border-border rounded-full text-[hsl(var(--brand-terra))] shrink-0 transition-transform duration-300 group-open:rotate-45">
                      +
                    </span>
                  </summary>
                  <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Maillage interne */}
        <section className="py-16 md:py-20 bg-secondary border-t border-border">
          <div className="container-wide">
            <p className="overline mb-6 text-center">Aller plus loin</p>
            <div className="grid gap-6 md:grid-cols-3 reveal">
              <Link
                to="/permis-de-construire-la-reunion"
                className="group block bg-white border border-border p-7 hover:-translate-y-1 hover:border-[hsl(var(--brand-terra))] transition-all duration-300"
              >
                <p className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--brand-terra))] font-semibold">
                  Page liée
                </p>
                <h3 className="mt-2 font-display text-xl text-primary">
                  Permis de construire à La Réunion
                </h3>
                <p className="mt-3 text-sm text-muted-foreground">
                  Dossier complet, conforme et prêt pour la mairie.
                </p>
                <ArrowRight size={16} className="mt-5 text-[hsl(var(--brand-terra))] transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/etude-geotechnique-la-reunion"
                className="group block bg-white border border-border p-7 hover:-translate-y-1 hover:border-[hsl(var(--brand-terra))] transition-all duration-300"
              >
                <p className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--brand-terra))] font-semibold">
                  Page liée
                </p>
                <h3 className="mt-2 font-display text-xl text-primary">
                  Étude géotechnique à La Réunion
                </h3>
                <p className="mt-3 text-sm text-muted-foreground">
                  Missions G1 à G5 normées et adaptées à votre projet.
                </p>
                <ArrowRight size={16} className="mt-5 text-[hsl(var(--brand-terra))] transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/qui-sommes-nous"
                className="group block bg-white border border-border p-7 hover:-translate-y-1 hover:border-[hsl(var(--brand-terra))] transition-all duration-300"
              >
                <p className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--brand-terra))] font-semibold">
                  Page liée
                </p>
                <h3 className="mt-2 font-display text-xl text-primary">
                  Bureau d'études à La Réunion
                </h3>
                <p className="mt-3 text-sm text-muted-foreground">
                  Qui sommes-nous, notre méthode et notre réseau.
                </p>
                <ArrowRight size={16} className="mt-5 text-[hsl(var(--brand-terra))] transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 md:py-32 bg-primary text-primary-foreground">
          <div className="container-narrow text-center reveal">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--brand-terra))] font-semibold">
              Lancez votre étude
            </p>
            <h2 className="mt-5 font-display text-5xl sm:text-6xl leading-[1.05]">
              Devis gratuit{" "}
              <span className="italic font-light text-white/80">en 24h</span>.
            </h2>
            <p className="mt-5 text-base md:text-lg text-white/70 max-w-xl mx-auto">
              Décrivez-nous votre projet, votre commune et la nature de votre
              terrain. Nous revenons vers vous rapidement avec une proposition
              personnalisée.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/#contact"
                className="group inline-flex items-center gap-2 bg-[hsl(var(--brand-terra))] text-white px-8 py-5 text-sm font-medium tracking-wide rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:bg-[hsl(var(--brand-terra)/0.9)]"
              >
                Demander un devis
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/#callback"
                className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-5 text-sm font-medium tracking-wide rounded-full transition-all duration-300 hover:bg-white hover:text-primary"
              >
                <Phone size={16} strokeWidth={1.6} />
                Être rappelé
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFloat />
      <StickyCTA />
    </div>
  );
}

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaProperty(prop, content) {
  let el = document.querySelector(`meta[property="${prop}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", prop);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
